import { useState } from 'react';

import { TotalPremiumVisibilityProvider } from '../../components/TotalPremium';

import { Header as DefaultShopHeader } from './components/Header';
import { PremiumBar } from './components/PremiumBar';
import { TotalPremiumProvider, UseCoverageFormProvider } from './useCoveragesForm';

import type { OnError, OnSubmit } from './useCoveragesForm';
import type { ContactOptionsProps } from '../../components/ContactOptions';
import type { RichContentType } from '../../components/RichContent';
import type { Fee, FeeType } from '../../models/fee';
import type { CountryCode } from '@insify/global-countries';
import type { Currency, MoneyField } from '@insify/global-money';
import type React from 'react';

/** maybe the coverage value need a type for 'toggle' compared to 'select' becuase they allow different values */
export type CoverageParamsValue = MoneyField | null | boolean;
export type CoveragePremiumValue = {
  amount?: MoneyField;
  amountBeforeDiscount?: MoneyField;
  amountDiscount?: MoneyField;
  amountTax?: MoneyField;
  percentageDiscount?: number;
};

export type CoverageParams<CoverageType extends string, PropertyMultiRiskType extends string = string> = {
  [Coverage in Exclude<CoverageType, 'propertyMultiRisk'>]?: CoverageParamsValue;
} & {
  deductible?: MoneyField;
  propertyMultiRisk?: {
    [Coverage in PropertyMultiRiskType]?: MoneyField;
  };
};
export type CoveragePremiums<CoverageType extends string> = {
  total?: CoveragePremiumValue;
  /** Only add the coverages that you want to show in the receipt list */
  coverages?: ({
    type: CoverageType;
  } & CoveragePremiumValue)[];
};

export type CoverageValues<CoverageType extends string, PropertyMultiRiskType extends string> = {
  defaultValues?: Partial<CoverageParams<CoverageType, PropertyMultiRiskType>>;
  /** The values that will update the provided inputs */
  params: CoverageParams<CoverageType, PropertyMultiRiskType>;
  /** The premiums that will update the receipt */
  premiums: CoveragePremiums<CoverageType>;
  fees?: Fee[];
  isLoading: boolean;
};

export type ShopPageEventHandlers = {
  eventHandlers: {
    onBack: () => void;
  };
};

export type PNCShopEventHandlers<CoverageType extends string, PropertyMultiRiskType extends string = string> = {
  eventHandlers: {
    onSelect: (coverage: { coverLimit: MoneyField; type: CoverageType }) => void;
    onSelectDeductible: (coverage: { amount: MoneyField }) => void;
    onToggle?: (coverage: { enabled: boolean; type: CoverageType }) => void;
    onPropertyChange?: (coverage: { type: PropertyMultiRiskType; amount: MoneyField }) => void;
    onSubmit: OnSubmit<CoverageType, PropertyMultiRiskType>;
    onError: OnError<CoverageType, PropertyMultiRiskType>;
  };
};

/** Due to the amount of values instead of slots these flags are hard to work around */
type ShopContentOptions = {
  hideHeaderPremium?: boolean;
};

export type ShopContentCommon = ContactOptionsProps &
  ShopContentOptions & {
    country: CountryCode | Lowercase<CountryCode>;
    currency: Currency;
    introContent?: RichContentType;
    receiptContent?: RichContentType;
    receiptDiscountContent?: React.ReactNode;
    receiptBelowSubmit?: React.ReactNode;
    translations: {
      back: string;
      perMonth: string;
      perYear: string;
      headerConditions: React.ReactNode;
      totalPremiumContinue: string;
      receiptTotal: string;
      receiptDeductible: string;
      receiptSubmit: string;
      receiptUSP?: React.ReactElement;
      fees?: Record<FeeType, { title: React.ReactNode; description: React.ReactNode }>;
    };
  };

export type CoverageContent<CoverageType extends string = string> = ShopContentCommon & {
  translations: ShopContentCommon['translations'] &
    Partial<{
      [Coverage in CoverageType as `receipt${Capitalize<Coverage>}`]: string;
    }>;
};

export type ShopProps = ShopContentCommon &
  ShopPageEventHandlers & {
    header?: React.ReactNode;
    isValid: boolean;
    children: React.ReactNode;
    premium?: CoveragePremiumValue;
    fees?: Fee[];
    isLoading: boolean;
    HeaderWrapper?: React.ComponentType<{ children: React.ReactNode }> | keyof JSX.IntrinsicElements;
  };

export const ShopPage = ({
  premium,
  children,
  isLoading = false,
  isValid,
  header,
  HeaderWrapper = 'div',
  ...content
}: ShopProps) => {
  const hasCustomHeader = header !== undefined;
  const shopHeader = hasCustomHeader ? header : <DefaultShopHeader {...content} />;
  return (
    <>
      {/*TODO Yankovsky rebranding*/}
      {/*<LayoutPage*/}
      {/*  onShowPrevious={eventHandlers.onBack}*/}
      {/*  className={styles.layout}*/}
      {/*  previousButtonCopy={content.translations.back}*/}
      {/*  previousButtonClassName={styles.previousButton}*/}
      {/*>*/}
      <TotalPremiumProvider premium={premium ?? {}} isLoading={isLoading}>
        <TotalPremiumVisibilityProvider>
          {/* @ts-ignore TODO error TS2741: Property ''case-id'' is missing in type */}
          <HeaderWrapper>{shopHeader}</HeaderWrapper>
          {children}
          <PremiumBar isValid={isValid} {...content} />
        </TotalPremiumVisibilityProvider>
      </TotalPremiumProvider>
      {/*</LayoutPage>*/}
    </>
  );
};

export type ShopPNCPageProps<
  CoverageType extends string,
  PropertyMultiRiskType extends string,
> = CoverageContent<CoverageType> &
  ShopPageEventHandlers &
  PNCShopEventHandlers<CoverageType, PropertyMultiRiskType> &
  Pick<ShopProps, 'isLoading' | 'children' | 'HeaderWrapper'> &
  CoverageValues<CoverageType, PropertyMultiRiskType>;

export const ShopPNC = <CoverageType extends string, PropertyMultiRiskType extends string>({
  eventHandlers,
  fees,
  premiums,
  children,
  isLoading = false,
  params,
  defaultValues,
  HeaderWrapper,
  ...content
}: ShopPNCPageProps<CoverageType, PropertyMultiRiskType>) => {
  const [isFormValid, setIsFormValid] = useState(false);

  return (
    // @ts-ignore TS2590: Expression produces a union type that is too complex to represent.
    <UseCoverageFormProvider
      params={params}
      defaultValues={defaultValues}
      eventHandlers={{
        onError: eventHandlers.onError,
        onSubmit: eventHandlers.onSubmit,
        onSelectDeductible: eventHandlers.onSelectDeductible,
        onSelect: eventHandlers.onSelect,
        onToggle: eventHandlers.onToggle,
        onPropertyChange: eventHandlers.onPropertyChange,
        onValidityChange: setIsFormValid,
      }}
    >
      <ShopPage
        isValid={isFormValid}
        isLoading={isLoading}
        premium={premiums.total}
        fees={fees}
        HeaderWrapper={HeaderWrapper}
        {...content}
        eventHandlers={eventHandlers}
      >
        {children}
      </ShopPage>
    </UseCoverageFormProvider>
  );
};
